// ============================================================
// ELO LEVELS — the 9-level skill table (names, ELO ranges, colors, Norwegian
// skill descriptions) + the getEloLevel(elo) lookup. Pure UI data + a pure
// helper, extracted verbatim from legacy/App.jsx so src/ views (player badges,
// single-matches, players list) can import them. Kept byte-identical.
// ============================================================

/** One ELO skill level (loosely typed — UI data table). */
export interface EloLevel {
  id: string;
  level: number;
  name: string;
  min: number;
  max: number;
  midpoint: number;
  color: string;
  text: string;
  short: string;
  description: string;
  skills: string[];
}

export const ELO_LEVELS: EloLevel[] = [
  {
    id: 'l1',
    level: 1,
    name: 'Nybegynner',
    min: 500,
    max: 750,
    midpoint: 600,
    color: '#dbeafe',
    text: '#1e3a8a',
    short: 'Helt ny i padel',
    description:
      'Akkurat startet å spille padel. Lærer hvordan racketen treffer ballen og hvor man står på banen.',
    skills: [
      'Ujevne treff, fokus på å få ballen over nettet',
      'Lærer reglene og grunnleggende posisjon',
      'Sliter med å holde ballen i spill',
      'Får sjelden serve over på riktig måte',
    ],
  },
  {
    id: 'l2',
    level: 2,
    name: 'Nybegynner pluss',
    min: 750,
    max: 1050,
    midpoint: 900,
    color: '#a5d8e8',
    text: '#155e75',
    short: 'Klarer korte rally',
    description:
      'Har spilt noen ganger og kan holde korte rally i rolig tempo. Begynner å samarbeide med makker.',
    skills: [
      'Holder ballen i spill 2-3 slag på rad i rolig tempo',
      'Får serve i spill mesteparten av tiden',
      'Begynner å samarbeide med makker',
      'Begrenset kontroll under press',
    ],
  },
  {
    id: 'l3',
    level: 3,
    name: 'Viderekommen nybegynner',
    min: 1050,
    max: 1350,
    midpoint: 1200,
    color: '#99d6c2',
    text: '#115e4a',
    short: 'Stabilt i moderat tempo',
    description:
      'Klarer stabile rally i moderat tempo. Begynner å bruke bakveggen bevisst og forstår nettposisjonering.',
    skills: [
      'Stabile rally i kontrollert tempo',
      'Begynner å bruke bakveggen bevisst',
      'Enkel forståelse av posisjonering frem/tilbake',
      'Kan spille enkle poeng uten store kollaps',
    ],
  },
  {
    id: 'l4',
    level: 4,
    name: 'Middels minus',
    min: 1350,
    max: 1650,
    midpoint: 1500,
    color: '#bce06a',
    text: '#3f6212',
    short: 'Kontroll på grunnslag',
    description:
      'Kontroll på grunnslag i forhånd og bakhånd. Kan spille hele kamper, men gjør fortsatt en del feil under press.',
    skills: [
      'Kontroll på forhånd og bakhånd i normalt tempo',
      'Kan spille hele kamper uten å falle helt fra hverandre',
      'Sliter når tempoet øker eller motstander varierer',
      'Begrenset taktisk spill — fokus på trygge slag',
    ],
  },
  {
    id: 'l5',
    level: 5,
    name: 'Middels',
    min: 1650,
    max: 1950,
    midpoint: 1800,
    color: '#f5d547',
    text: '#854d0e',
    short: 'Stabil og taktisk',
    description:
      'Stabil i kampspill. Bruker lob og vegg med hensikt. Typisk midtnivå i klubben — mange spillere finner sin platå her.',
    skills: [
      'Stabil i normalt kamppress',
      'Bruker lob og vegg taktisk for å vinne posisjon',
      'Forstår posisjonering frem og tilbake mellom makker',
      'Spiller jevne kamper mot lignende nivå',
      'Typisk midtnivå i en klubb',
    ],
  },
  {
    id: 'l6',
    level: 6,
    name: 'Middels pluss',
    min: 1950,
    max: 2250,
    midpoint: 2100,
    color: '#f5963f',
    // #1144 (owner decision 2026-11-02): was #7c2d12, 4.16:1 on #f5963f.
    text: '#75260a',
    short: 'God kontroll og variasjon',
    description:
      'God kontroll og variasjon i slagene. Tryggere i nettspill og overhead. Spiller bevisst og taktisk.',
    skills: [
      'Variasjon i slagene (slice, flate, lobb)',
      'Tryggere i nettspill og overhead-smash',
      'Spiller mer bevisst og taktisk enn middel',
      'Færre uforklarlige feil under press',
      'Bygger poeng med plan',
    ],
  },
  {
    id: 'l7',
    level: 7,
    name: 'Viderekommen',
    min: 2250,
    max: 2550,
    midpoint: 2400,
    // #1144 (owner decision 2026-11-02): was #e0533a, 3.84:1 under white.
    color: '#d1452d',
    text: '#ffffff',
    short: 'Stabil under press',
    description:
      'Stabil også under press. Bygger poeng aktivt og har god forståelse for spillmønstre. Komfortabel i turneringer.',
    skills: [
      'Stabil under turneringspress',
      'Bygger poeng aktivt og bevisst',
      'God forståelse for spillmønstre og motstanders taktikk',
      'Komfortabel i klubbturneringer',
      'Mestrer víbora og bandeja som angrepsverktøy',
    ],
  },
  {
    id: 'l8',
    level: 8,
    name: 'Avansert',
    min: 2550,
    max: 2850,
    midpoint: 2700,
    color: '#b3293b',
    text: '#ffffff',
    short: 'En av de beste i klubben',
    description:
      'En av de beste spillerne i klubben. Høy kvalitet i alle slag. Spiller taktisk riktig og stabilt, og kan utfordre de aller beste.',
    skills: [
      'Høy kvalitet i alle slagtyper',
      'Avansert veggspill (dobbel-vegg, x-bandeja)',
      'Taktisk overlegen mot lavere nivåer',
      'Komfortabel i regionale turneringer',
      'Bygger og avslutter poeng med presisjon',
    ],
  },
  {
    id: 'l9',
    level: 9,
    name: 'Toppnivå i klubben',
    min: 2850,
    max: 9999,
    midpoint: 3000,
    color: '#6b2d6b',
    text: '#ffffff',
    short: 'Setter nivået i klubben',
    description:
      'De aller beste spillerne i klubben. Svært stabil og komplett spiller. Vinner jevnlig mot nivå 8 og setter klubbens standard.',
    skills: [
      'Mestrer alle slagtyper på elite-nivå',
      'Mental styrke i tette og avgjørende kamper',
      'Leser kamper og motstandere intuitivt',
      'Regional og nasjonal turneringsnivå',
      'Setter klubbens spillstandard',
    ],
  },
];

// Get the level based on the ELO number
export function getEloLevel(elo: number): EloLevel {
  return ELO_LEVELS.find((l) => elo >= l.min && elo < l.max) || ELO_LEVELS[ELO_LEVELS.length - 1];
}
